import type { FC} from 'react';
import {
  Box,
  Divider,
  Drawer,
  List,
  Toolbar,
  useTheme,
} from '@/shared/ui/mui';
import type { User } from '@/shared/types';
import type { MenuItem as MenuItemType, SideMenuProps } from '../types';
import LogoutButton from './LogoutButton';
import MenuItem from './MenuItem';
import UserProfile from './UserProfile';

interface ExtendedSideMenuProps extends SideMenuProps {
  variant?: 'permanent' | 'temporary' | 'persistent';
  menuItems: MenuItemType[];
  commonItems: MenuItemType[];
  user: User | null;
}

export const SideMenu: FC<ExtendedSideMenuProps> = ({
  open,
  onClose,
  userType,
  variant = 'temporary',
  menuItems,
  commonItems,
  user,
}) => {
  const theme = useTheme();

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      variant={variant}
      sx={{
        '& .MuiDrawer-paper': {
          width: 280,
          boxSizing: 'border-box',
          backgroundColor: theme.palette.common.white,
          borderRight: '1px solid',
          borderColor: theme.palette.grey[200],
          zIndex: variant === 'permanent'
            ? theme.zIndex.appBar - 1
            : theme.zIndex.appBar + 1,
        },
        zIndex: variant === 'permanent'
          ? theme.zIndex.appBar - 1
          : theme.zIndex.drawer + 2,
      }}
    >
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {variant === 'permanent' && <Toolbar />}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderBottom: '1px solid',
            borderColor: theme.palette.grey[200],
          }}
        >
          <UserProfile user={user} userType={userType} />
        </Box>

        <List sx={{ pt: 0, flexGrow: 1 }}>
          {menuItems.map((item) => (
            <MenuItem
              key={item.text}
              icon={item.icon}
              text={item.text}
              labelKey={item.labelKey}
              path={item.path}
              subItems={item.subItems}
              onClose={onClose}
            />
          ))}

          {commonItems.length > 0 && (
            <>
              <Divider sx={{ my: 1 }} />
              {commonItems.map((item) => (
                <MenuItem
                  key={item.text}
                  icon={item.icon}
                  text={item.text}
                  labelKey={item.labelKey}
                  path={item.path}
                  onClose={onClose}
                />
              ))}
            </>
          )}
        </List>

        <Box sx={{ bgcolor: theme.palette.grey[50] }}>
          <Divider />
          <LogoutButton onClose={onClose} />
        </Box>
      </Box>
    </Drawer>
  );
};
